import { useState, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, t } from '@lingui/macro';
import { Button, Radio, RadioGroup, MenuItem, FormControlLabel } from '@material-ui/core';
import { extend, compact } from 'lodash';
import { useId } from '../../page-framework/panel/Panel';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpSelect from '../../form/NgpSelect';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';
import { useDataService } from '../../../services/ApiDataService';
import { useFindById } from '../../../services/FormQueryService';
import { useFormSaveService } from '../../../services/FormSaveService';
import { NetTestNowAgentsFormSection } from './NetTestNowAgentsFormSection';
import { useDetailStyles } from './NetTestNowStyles';
import { NetTestNowAgentTagSelect } from './NetTestNowAgentTagSelect';
import { filterAgentsBySelectedTags, isWirelessAgent } from './NetTestNowHelpers';
import { useNavigate } from '../../../utilities/PgmUtilities';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';

const testsListApiUrl = '/api/v3/service-tests/list';
const nPointsListApiUrl = '/query/admin/agents/picker';
const quickTestsApiUrl = '/ipm/v1/admin/quicktests';
const defaultValues = { agents: [], tags: [], tagLogic: 'any', shouldUnregister: false };

export default function NetTestNowDetailPanel({ reRun, tableSelection, submitRef, setBreadcrumbs }) {
    const id = useId();
    const styles = useDetailStyles();
    const [testList, setTestList] = useState([]);
    const [agentList, setAgentList] = useState([]);
    const [wirelessAgentList, setWirelessAgentList] = useState([]);
    // Per-interface selection history, mirroring the portal/ui testAgentsPicker's selectedListHistory:
    // each adapter type (wired / wireless) remembers its own selection independently across switches.
    const selectionHistoryRef = useRef({});
    const [loading, setLoading] = useState(true);
    const [agentSelectionIsDirty, setAgentSelectionIsDirty] = useState(false);
    const methods = useForm({ defaultValues });
    const { reset, setError, setValue, getValues } = methods;
    const formSaveService = useFormSaveService();
    const [nPointDisplayType, setNpointDisplayType] = useChartDisplayType(`${id}-nPointSelector`, 'all');
    const [interfaceType, setInterfaceType] = useChartDisplayType(`${id}-interfaceType`, 'wired');
    const dataService = useDataService();
    const { selection } = tableSelection || null;
    const navigate = useNavigate();
    const [didError, setDidError] = useState(false);

    const testQueryParams = { testType: 'Bandwidth' };
    const goToUrl = '/net-test-now';
    const functionBreadCrumbs = [{ link: goToUrl, text: t`Bandwidth Test Now` }];
    const reportingLinkConfig = {
        target: '/net-test-now',
    };
    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, null, {}, 'name', t`Test Details`, {
        reportingLinkConfig,
    });

    useEffect(() => {
        (async () => {
            const { data } = await dataService.get(testsListApiUrl, testQueryParams);
            const agentData = await dataService.get(nPointsListApiUrl);
            const runningAgents = agentData?.filter((agents) => agents?.status.toLowerCase() === 'running');
            const wirelessAgents = runningAgents?.filter((agent) => isWirelessAgent(agent));

            setBreadcrumbs(breadcrumbs);

            // Don't try to run a wireless test on nPoints that don't have a wireless interface or are set to Multi-SSID mode
            setWirelessAgentList(wirelessAgents);
            setAgentList(runningAgents);
            setLoading(false);
            setTestList(data);

            // set form fields and required state variables for when a test is chosen to re-run.
            if (reRun && selection) {
                const quickTests = await dataService.get(quickTestsApiUrl);
                const reRunTestInfo = await quickTests?.filter((testData) => testData?._id === selection[0]).shift();
                const { name, adapterType } = reRunTestInfo;

                setValue('name', name);
                setInterfaceType(adapterType);

                // This test only currently works with existing tests.
                if (reRunTestInfo.existingTestId) {
                    const theExistingTest = data.find((test) => test.id === reRunTestInfo.existingTestId);
                    setValue('selectedTest', theExistingTest);
                }

                // To avoid submitting 'run on npoints' selection, the exact test can be re-run by selecting all of the nPoints that were previously used,
                //  even if they were previously selected by choosing 'all' or tagged nPoints.
                setNpointDisplayType('select');

                // Don't send the test to Offline or Disabled nPoints.
                const runningPreviousAgents = compact(
                    runningAgents.map((agent) => {
                        if (reRunTestInfo.agents.ids.includes(agent._id)) {
                            return agent._id;
                        }
                        // the compact function will remove nulls.
                        return null;
                    })
                );

                // avoid setting previous agents to an empty array.
                if (runningPreviousAgents) {
                    // Seed the history for the re-run's adapter so a later interface switch can restore it.
                    selectionHistoryRef.current[adapterType] = runningPreviousAgents;
                    setValue('agents', runningPreviousAgents);
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataService, reRun, tableSelection, setInterfaceType, selection, setNpointDisplayType, setValue]);

    /**
     * Returns list of agents based on the nPoint display type
     * @param {Object} form
     * @returns
     */
    const getAgents = (form) => {
        // wireless agents and running agents are pre filtered in the initial creation of agentList or wirelessAgentList
        if (nPointDisplayType === 'tagged') {
            const { tagLogic } = form;
            if (interfaceType === 'wireless') {
                return filterAgentsBySelectedTags(wirelessAgentList, form.tags, tagLogic);
            }
            return filterAgentsBySelectedTags(agentList, form.tags, tagLogic);
        }

        // the nPoint picker can return disabled nPoints in its query, filter out anything that isn't in the pre-filtered agentList or wirelessAgentList.
        if (nPointDisplayType === 'select') {
            if (interfaceType === 'wireless') {
                const noDisabled = wirelessAgentList?.filter((agent) => form.agents?.includes(agent._id));
                return noDisabled;
            }
            const noDisabled = agentList?.filter((agent) => form.agents?.includes(agent._id));
            return noDisabled;
        }
        // if 'all nPoints' nothing changes.

        if (interfaceType === 'wireless') {
            return wirelessAgentList;
        }
        return agentList;
    };

    const dirtyCheck = (isDirty) => isDirty || agentSelectionIsDirty;

    const { initialized, finding, formData } = useFindById({
        url: nPointsListApiUrl,
        resetFunc: reset,
        defaultValues,
    });

    const handleFormSubmit = async (submittedFormData) => {
        const { selectedTest } = submittedFormData;
        const agents = getAgents(submittedFormData, agentList, interfaceType);
        // optional parameters come as undefined if they are not specified. They need to be specifically null.
        selectedTest.parameters.port_max = selectedTest?.parameters?.port_max || null;
        selectedTest.parameters.packet_size = selectedTest?.parameters?.packet_size || null;
        extend(selectedTest.parameters, { auth: null });

        const tformData = {
            name: selectedTest.name,
            adapterType: interfaceType,
            agentResults: agents.map((agent) => ({ agent: agent._id || agent, resultCount: 0, status: 'I' })) || [],
            agents: { ids: agents.map((agent) => agent._id || agent) },
            beaconPackets: false,
            testConfig: selectedTest?.parameters,
            existingTestId: selectedTest?.id,
            type: selectedTest?.testType?.id,
            quickTestType: 'net',
            verify: false,
        };

        // Form verification -- don't save the form if there are no nPoints selected.
        const formValues = getValues();
        if (formValues.agents?.length || agents.length) {
            // navigate(`/net-test-now/`)

            // Go straight to results page for this test run on submission
            return formSaveService
                .save({
                    url: '/ipm/v1/admin/quicktests',
                    dataToSave: tformData,
                    setError,
                })
                .then((response) => navigate(`${response._id}`));
        }
        // No nPoints Selected, don't submit and show an error. Set agentSelectionIsDirty to false so that the error goes away after they select an nPoint.
        setDidError(true);
        setAgentSelectionIsDirty(false);
    };

    // remove the error and also set the value of the agents field to [] so that they cannot successfully submit the form even though it doesn't show any nPoints as being selected.
    const handleSelectionTypeChange = (e) => {
        setNpointDisplayType(e.target.value);
        setAgentSelectionIsDirty(false);
        setDidError(false);
        setValue('agents', []);
    };

    // On interface change, save the current adapter's selection and restore the new adapter's previously saved
    // selection, matching the portal/ui testAgentsPicker's per-adapter history behavior.
    const handleInterfaceChange = (e) => {
        const newInterface = e.target.value;
        const oldInterface = interfaceType;
        const { agents } = getValues();

        // if 0 wireless nPoints are available and the error is flagged, remove it when the change to the wired interface.
        if (nPointDisplayType === 'all') {
            setAgentSelectionIsDirty(true);
        }

        if (oldInterface) {
            selectionHistoryRef.current[oldInterface] = agents || [];
        }

        setInterfaceType(newInterface);

        // Restore the previously saved selection for the new interface, filtered to ids still valid
        // for that interface (e.g. an nPoint may have lost wireless capability between switches).
        const validIds = (newInterface === 'wireless' ? wirelessAgentList : agentList)?.map((a) => a._id) ?? [];
        const restored = (selectionHistoryRef.current[newInterface] || []).filter((agentId) =>
            validIds.includes(agentId)
        );
        setValue('agents', restored);
    };

    return (
        <FormProvider {...methods} flexHeight>
            <NgpForm
                formId="net-test-now-form"
                initialized={initialized}
                loading={finding || loading}
                showSave={false}
                formPadding={false}
                showRequiredFieldsTip={false}
                submitFunction={handleFormSubmit}
                isDirtyFunc={dirtyCheck}
            >
                <FormSection title={<Trans>Test options</Trans>}>
                    <NgpSelect
                        fieldName="selectedTest"
                        label={t`Select Existing Test`}
                        required
                        className={styles.testSelect}
                        helpTipText="Choose a pre-existing bandwidth test to run"
                    >
                        {testList.map((test) => (
                            <MenuItem key={test.id} value={test}>
                                {test.name}
                            </MenuItem>
                        ))}
                    </NgpSelect>
                </FormSection>
                <FormSection title={<Trans>Run On nPoints</Trans>}>
                    <RadioGroup
                        className={styles.radioWrapper}
                        value={nPointDisplayType}
                        onChange={(e) => handleSelectionTypeChange(e)}
                    >
                        <FormControlLabel value="all" label={t`Run on all nPoints`} control={<Radio />} />
                        <FormControlLabel value="select" label={t`Run on select nPoints`} control={<Radio />} />
                        <FormControlLabel value="tagged" label={t`Run on tagged nPoints`} control={<Radio />} />
                    </RadioGroup>
                    {nPointDisplayType === 'all' && (
                        <>
                            {didError && !agentSelectionIsDirty && (
                                <h3 className={styles.selectionError}>You must select at least one nPoint.</h3>
                            )}
                            <Trans>
                                <h3 style={{ color: 'green' }}>
                                    {interfaceType === 'wireless' ? wirelessAgentList.length : agentList.length} nPoints
                                    ready to run test{' '}
                                </h3>
                            </Trans>
                        </>
                    )}
                    {nPointDisplayType === 'select' && (
                        <>
                            {didError && !agentSelectionIsDirty && (
                                <h3 className={styles.selectionError}>You must select at least one nPoint.</h3>
                            )}
                            <NetTestNowAgentsFormSection
                                formData={formData}
                                setAgentSelectionIsDirty={setAgentSelectionIsDirty}
                                interfaceType={interfaceType}
                                agentList={agentList}
                                wirelessAgentList={wirelessAgentList}
                            />
                        </>
                    )}
                    {nPointDisplayType === 'tagged' && (
                        <>
                            {didError && !agentSelectionIsDirty && (
                                <h3 className={styles.selectionError}>You must select at least one nPoint.</h3>
                            )}
                            <NetTestNowAgentTagSelect
                                agentList={agentList}
                                setAgentSelectionIsDirty={setAgentSelectionIsDirty}
                                interfaceType={interfaceType}
                            />
                        </>
                    )}
                </FormSection>
                <FormSection title={<Trans>nPoint Interface</Trans>}>
                    <RadioGroup
                        className={styles.radioWrapper}
                        value={interfaceType}
                        onChange={(e) => handleInterfaceChange(e)}
                    >
                        <FormControlLabel value="wired" label="Run on nPoint wired interface" control={<Radio />} />
                        <FormControlLabel
                            value="wireless"
                            label={t`Run on hardware nPoint wireless interface`}
                            control={<Radio />}
                        />
                    </RadioGroup>
                </FormSection>
                <Button
                    ref={submitRef}
                    color="primary"
                    variant="contained"
                    size="large"
                    onClick={methods.handleSubmit(handleFormSubmit)}
                >
                    <Trans>Run Test</Trans>
                </Button>
            </NgpForm>
        </FormProvider>
    );
}
